import { useEffect, useRef, useState } from "react";
import type { AdPlacement, PublicAdsConfig } from "@serendiptv/contracts";
import { adDiagnostic } from "../lib/usage";
import { fits, placementSize, type AdSize } from "./layout";
import { loadProvider, requestAllowed, useConsentState } from "./provider";
import "./ads.css";

type PlacementRecord = { size?: AdSize; diagnosed: Set<string>; attempted: boolean; retired: boolean; node?: HTMLElement; identity?: string; observer?: MutationObserver };
// Never reset on config polling, remount, consent changes, or resizing.
const records = new Map<AdPlacement, PlacementRecord>();
function recordFor(placement: AdPlacement) {
  let record = records.get(placement);
  if (!record) { record = { diagnosed: new Set(), attempted: false, retired: false }; records.set(placement, record); }
  return record;
}
function diagnose(placement: AdPlacement, kind: Parameters<typeof adDiagnostic>[0]) {
  const record = recordFor(placement);
  if (!record.diagnosed.has(kind)) { record.diagnosed.add(kind); adDiagnostic(kind, placement); }
}

function availableSize(parent: HTMLElement, placement: AdPlacement, eligible: boolean) {
  const rect = parent.getBoundingClientRect();
  const style = getComputedStyle(parent);
  const width = rect.width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  const occupied = placement === "cabinet" ? [...parent.querySelectorAll(".station-tuner,.now-playing-module,.panel-actions")].reduce((sum, element) => sum + element.getBoundingClientRect().height, 0) + 4 * parseFloat(style.rowGap || "0") + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) : 0;
  return eligible && document.visibilityState !== "hidden" ? placementSize(placement, width, rect.height, occupied) : null;
}

export function AdSlot({ placement, config, eligible }: { placement: AdPlacement; config: PublicAdsConfig; eligible: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const creative = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<AdSize | null>(null);
  const [providerReady, setProviderReady] = useState(false);
  const consent = useConsentState();
  const record = recordFor(placement);
  const enabled = config.mode !== "off" && config[`${placement}_enabled`];
  const identity = `${config.publisher_id}:${config[`${placement}_unit_id`]}`;

  useEffect(() => {
    const parent = host.current?.parentElement;
    if (!parent) return;
    const measure = () => {
      const available = availableSize(parent, placement, eligible);
      if (available) diagnose(placement, "ad_eligible");
      if (enabled && available && !record.size) record.size = available;
      const next = enabled && record.size && fits(record.size, available) ? record.size : null;
      if (next && config.mode === "preview") diagnose(placement, "ad_preview");
      // Reconsider the first request on every layout observation. A rapid
      // shrink/expand can be coalesced by ResizeObserver, leaving the chosen
      // fixed size unchanged even though a consent callback saw cramped space.
      setSize(next ? { ...next } : null);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    if (placement === "cabinet") for (const element of parent.querySelectorAll(".station-tuner,.now-playing-module,.panel-actions")) observer.observe(element);
    window.addEventListener("resize", measure);
    document.addEventListener("visibilitychange", measure); measure();
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); document.removeEventListener("visibilitychange", measure); };
  }, [config.mode, eligible, enabled, placement, record]);

  useEffect(() => {
    if (record.attempted && (!enabled || config.mode !== "live" || consent !== "allowed" || identity !== record.identity)) {
      // Revocation/off is a terminal teardown, never a new request opportunity.
      record.retired = true; record.observer?.disconnect(); record.node?.replaceChildren(); record.node?.remove();
    }
    if (size && consent === "denied" && enabled && config.mode === "live") diagnose(placement, "ad_blocked");
    if (config.mode !== "live" || !enabled || !eligible || !size || record.retired) return;
    let active = true;
    void loadProvider(config).then(() => { if (active) setProviderReady(true); }).catch(() => { if (active) diagnose(placement, "ad_error"); });
    return () => { active = false; };
  }, [config, consent, enabled, eligible, identity, placement, record, size]);

  useEffect(() => {
    const container = creative.current;
    if (!container || !size || !providerReady || !enabled || !requestAllowed(config, placement) || record.attempted || record.retired) return;
    const parent = host.current?.parentElement;
    if (!parent || !fits(size, availableSize(parent, placement, eligible))) return;
    const unit = config[`${placement}_unit_id`];
    if (!unit || !config.publisher_id) return;
    const node = document.createElement("ins");
    node.className = "adsbygoogle";
    node.style.cssText = `display:block;width:${size.width}px;height:${size.height}px`;
    node.dataset.adClient = config.publisher_id; node.dataset.adSlot = unit;
    // Fixed dimensions, no responsive format/auto sizing and no refresh code.
    container.append(node); record.node = node; record.identity = identity;
    record.attempted = true;
    diagnose(placement, "ad_requested");
    const observer = new MutationObserver(() => {
      if (node.dataset.adStatus === "filled") diagnose(placement, "ad_filled");
      if (node.dataset.adStatus === "unfilled") diagnose(placement, "ad_unfilled");
    });
    record.observer = observer;
    observer.observe(node, { attributes: true, attributeFilter: ["data-ad-status"] });
    try { window.adsbygoogle!.push({}); } catch { diagnose(placement, "ad_error"); }
    // No removal/reparenting during effect cleanup: hidden iframe subtrees stay
    // continuously connected across tuning, game mode, layout and chat changes.
    // Observer is owned by the document record and ends only at terminal teardown.
  }, [config, consent, enabled, eligible, identity, placement, providerReady, record, size]);

  const active = Boolean(enabled && eligible && size && (config.mode === "preview" || config.mode === "live" && consent === "allowed" && !record.retired && (!record.attempted || record.identity === identity)));
  return <div ref={host} className={`display-ad-slot display-ad-${placement}`} data-active={active} data-placement={placement} aria-hidden={!active} aria-label={`${placement === "guide" ? "TV Guide" : "Cabinet"} advertisements`}>
    <span className="display-ad-label">Advertisements</span>
    <div hidden={config.mode !== "preview"} className="display-ad-preview" style={{ width: record.size?.width, height: record.size?.height }}><span>Placement preview</span><small>{record.size?.width} × {record.size?.height}</small></div>
    <div hidden={config.mode !== "live"} className="display-ad-creative" ref={creative} style={{ width: record.size?.width, height: record.size?.height }} />
  </div>;
}
